/**
 * Traffic analysis utilities for route planning
 */

export type TrafficSeverity = 'none' | 'light' | 'moderate' | 'heavy';

/**
 * Calculate traffic severity based on duration difference
 * @param {string} [duration] - Current route duration with traffic
 * @param {string} [staticDuration] - Static route duration without traffic
 * @return {TrafficSeverity} Traffic severity level
 */
export const getTrafficSeverity = (duration?: string, staticDuration?: string): TrafficSeverity => {
    if (!duration || !staticDuration) {
        return 'none';
    }

    const currentSeconds = parseInt(duration.replace('s', ''));
    const normalSeconds = parseInt(staticDuration.replace('s', ''));

    // Calculate percentage increase due to traffic
    const trafficDelay = currentSeconds - normalSeconds;
    const percentIncrease = (trafficDelay / normalSeconds) * 100;

    // Categorize based on delay percentage
    if (percentIncrease <= 5) {
        return 'none'; // Green - No significant traffic
    } else if (percentIncrease <= 25) {
        return 'light'; // Light green - Light traffic
    } else if (percentIncrease <= 50) {
        return 'moderate'; // Orange - Moderate traffic
    } else {
        return 'heavy'; // Red - Heavy traffic
    }
};

/**
 * Get color based on traffic severity
 * @param {TrafficSeverity} severity - Traffic severity level
 * @return {string} Hex color code for the severity level
 */
export const getTrafficColor = (severity: TrafficSeverity): string => {
    switch (severity) {
        case 'none':
            return '#10b981'; // Green
        case 'light':
            return '#10b981'; // Light green
        case 'moderate':
            return '#f59e0b'; // Orange
        case 'heavy':
            return '#ef4444'; // Red
        default:
            return '#1f2937'; // Default dark gray
    }
};

/**
 * Get traffic status text
 * @param {TrafficSeverity} severity - Traffic severity level
 * @return {string} Human readable traffic status text
 */
export const getTrafficStatus = (severity: TrafficSeverity): string => {
    switch (severity) {
        case 'none':
            return 'Clear';
        case 'light':
            return 'Light traffic';
        case 'moderate':
            return 'Moderate traffic';
        case 'heavy':
            return 'Heavy traffic';
        default:
            return '';
    }
};

/**
 * Calculate traffic delay in minutes
 * @param {string} [duration] - Current route duration with traffic
 * @param {string} [staticDuration] - Static route duration without traffic
 * @return {number} Traffic delay in minutes
 */
export const getTrafficDelay = (duration?: string, staticDuration?: string): number => {
    if (!duration || !staticDuration) {
        return 0;
    }

    const currentSeconds = parseInt(duration.replace('s', ''));
    const normalSeconds = parseInt(staticDuration.replace('s', ''));

    return Math.round((currentSeconds - normalSeconds) / 60);
};

/**
 * Get traffic icon based on severity
 * @param {TrafficSeverity} severity - Traffic severity level
 * @return {string} Emoji icon representing the traffic severity
 */
export const getTrafficIcon = (severity: TrafficSeverity): string => {
    switch (severity) {
        case 'none':
            return '🟢';
        case 'light':
            return '🟡';
        case 'moderate':
            return '🟠';
        case 'heavy':
            return '🔴';
        default:
            return '';
    }
};

/**
 * Format duration with traffic color
 * @param {string} duration - Route duration to format
 * @param {string} [staticDuration] - Static duration for traffic comparison
 * @return {object} Formatted duration with color, severity, and delay info
 */
export const formatDurationWithTraffic = (duration: string, staticDuration?: string): {
    text: string;
    color: string;
    severity: TrafficSeverity;
    delay: number;
} => {
    const seconds = parseInt(duration.replace('s', ''));
    let text = '';

    if (seconds < 60) {
        text = `${seconds} sec`;
    } else {
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) {
            text = `${minutes} min`;
        } else {
            const hours = Math.floor(minutes / 60);
            const remainingMinutes = minutes % 60;
            text = remainingMinutes === 0 ? `${hours} hr` : `${hours} hr ${remainingMinutes} min`;
        }
    }

    const severity = getTrafficSeverity(duration, staticDuration);
    const color = getTrafficColor(severity);
    const delay = getTrafficDelay(duration, staticDuration);

    return {text, color, severity, delay};
};
